import { comp, filter, transduce, push } from "@thi.ng/transducers";
import { accessors } from ".";

export const isTreeFilter = (obj) => obj?.all;
export const isValueFilter = (obj) => obj?.value;

/**
 * This partial function checks whether an event's date
 * falls within a certain time range.
 *
 * @param {[Date, Date]} param1
 * @param {function} dateAccessor function for accessing the date value
 */
export const filterInDateRange =
  ([filterStart, filterEnd], dateAccessor) =>
  (d) => {
    const date = dateAccessor(d).getTime();
    const start = filterStart.getTime();
    const end = filterEnd.getTime();

    return start <= date && date <= end;
  };

/**
 * Text search across multiple columns that may or may not
 * contain arrays
 * @param {string} searchTerm
 * @param {[function]} fieldAccessors
 * @returns
 */
export const filterText = (searchTerm, fieldAccessors) => (d) => {
  const concatFields = fieldAccessors
    .map((a) => {
      const v = a(d);
      return Array.isArray(v) ? v.join(" ") : v;
    })
    .join(" ")
    .toLowerCase();
  return concatFields.indexOf(String(searchTerm || "").toLowerCase()) >= 0;
};

/**
 * Checks if one or more elements from a row's array are included within
 * the specified array.
 *
 * Works for finding the OR existence of single (or multiple)
 * values in an array.
 *
 * @param {Array} arr these act as the acting filters
 * @param {function} accessor function for accessing the data field's values
 */
export const filterInArray = (arr, accessor) => (d) => {
  const value = accessor(d);
  const toCompare = Array.isArray(value) ? value : [value];

  return toCompare.reduce((acc, curr) => acc || arr.indexOf(curr) >= 0, false);
};

/**
 * Same as previous but excludes provided items in array
 *
 * @param {Array} arr these act as the acting filters
 * @param {function} accessor function for accessing the data field's values
 */
export const filterExcludeFromArray = (arr, accessor) => (d) => {
  const value = accessor(d);
  const toCompare = Array.isArray(value) ? value : [value];

  return toCompare.reduce((acc, curr) => acc || arr.indexOf(curr) < 0, false);
};

/**
 * If predicate value is present within predicate function,
 * then filter consequent value on consequent function
 *
 * @param {function} predicateFunc
 * @param {function} predicateVal
 * @param {function} consequentFunc
 * @param {function} consequentVal
 * @returns bool
 */
export const filterWhenPredicate =
  (predicateFunc, predicateVal, consequentFunc, consequentVal) => (d) => {
    const attributeTypes = Array.isArray(consequentVal)
      ? consequentVal
      : [consequentVal];
    if (predicateFunc(d) === predicateVal) {
      if (attributeTypes.indexOf(consequentFunc(d)) >= 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

/**
 * Multi-dimensional filtering using an arbitrary number of
 * higher-order filter functions. Read here for more information:
 * https://medium.com/@thi.ng/of-umbrellas-transducers-reactive-streams-mushrooms-pt-2-9c540beb0023
 *
 * @param {*} data
 * @param  {...any} filters
 */
export const filterMulti = (data, ...filters) => {
  return transduce(comp(...filters.map((f) => filter(f))), push(), data);
};

export const filterContextualEvents = (d) => accessors.target.association(d);
export const filterTargetedEvents = (d) => accessors.target.name(d);
export const filterAssociation = (val) => (d) =>
  accessors.association(d).indexOf(val) >= 0;
export const filterGlobalEvents = (d) =>
  !accessors.target.association(d) && !accessors.target.name(d);
